import type { BaseLogger, UnknownRecord } from '../types'
import { flatten } from 'flat'
import type { IncenseEncryptionService } from '../services/IncenseEncryptionService'
import {
  IncenseEncryptionFactory,
  DEFAULT_ENCRYPTION_CONFIG,
} from '../services/IncenseEncryptionFactory'
import { getIncenseApiBaseUrl } from '../utils/getIncenseApiBaseUrl'
import type { LoggerAdapterConfig } from '@strata-monorepo/shared-types'

// Use type assertion to access newrelic without conflicting declarations
interface WindowWithNewRelic {
  newrelic?: {
    noticeError: (error: Error, metadata?: Record<string, unknown>) => void
    addPageAction?: (name: string, attributes?: Record<string, unknown>) => void
    recordCustomEvent?: (eventType: string, attributes: Record<string, unknown>) => void
    setCustomAttribute?: (name: string, value: string | number | boolean) => void
  }
}

/**
 * @class NewRelicBrowserAdapter - New Relic Browser adapter with encryption
 *
 * Provides a `BaseLogger` compliant implementation that proxies each
 * log level to its matching `newrelic` call. Sensitive data is encrypted
 * before logging to prevent accidental exposure.
 */
export class NewRelicBrowserAdapter implements BaseLogger {
  private encryptionService: IncenseEncryptionService
  private config: LoggerAdapterConfig

  constructor(config: LoggerAdapterConfig) {
    this.config = config

    if (
      typeof window === 'undefined' ||
      typeof (window as WindowWithNewRelic).newrelic === 'undefined'
    ) {
      throw new Error('New Relic browser agent is not available on window.newrelic')
    }

    this.encryptionService = IncenseEncryptionFactory.create({
      ...DEFAULT_ENCRYPTION_CONFIG,
      baseUrl: this.config.incenseBackendUrl ?? getIncenseApiBaseUrl(this.config.environment),
    })
  }

  private async sendError(
    error: Error,
    logLevel: 'error' | 'log' | 'warn' | 'info' | 'debug',
    metadata?: UnknownRecord,
  ): Promise<void> {
    const processedMetadata = { ...(metadata ?? {}) }

    // Handle sensitive data encryption
    if (processedMetadata['sensitive']) {
      try {
        const sensitiveData = processedMetadata['sensitive'] as Record<string, unknown>
        const result = await this.encryptionService.encryptSensitiveData(sensitiveData)

        // Replace sensitive data with encryption ID only
        processedMetadata['sensitive'] = result.sensitiveId
      } catch (encryptionError) {
        // On encryption failure, remove sensitive data to prevent exposure
        delete processedMetadata['sensitive']
        console.warn('[NewRelicBrowserAdapter] Failed to encrypt sensitive data:', encryptionError)
      }
    }

    const windowWithNewRelic = window as WindowWithNewRelic
    if (windowWithNewRelic.newrelic) {
      windowWithNewRelic.newrelic.noticeError(
        error,
        flatten({
          ...processedMetadata,
          logLevel,
          userType: this.config.userType,
        }),
      )
    }
  }

  async error(error: Error, metadata?: UnknownRecord): Promise<void> {
    await this.sendError(error, 'error', metadata)
  }

  async log(error: Error, metadata?: UnknownRecord): Promise<void> {
    await this.sendError(error, 'log', metadata)
  }

  async warn(error: Error, metadata?: UnknownRecord): Promise<void> {
    await this.sendError(error, 'warn', metadata)
  }

  async info(error: Error, metadata?: UnknownRecord): Promise<void> {
    await this.sendError(error, 'info', metadata)
  }

  async debug(error: Error, metadata?: UnknownRecord): Promise<void> {
    await this.sendError(error, 'debug', metadata)
  }
}
